import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import Toast from '../../components/Toast/Toast';
import ConfirmationModal from '../../components/modals/ConfirmationModal';
import ImageUploadModal from '../../components/ImageUploadModal/ImageUploadModal';
import { getProductDetailsAPI, uploadImageAPI, deleteImageAPI } from '../../utils/api';
import "./ProductImageUpload.css";
import { FaArrowLeft } from "react-icons/fa";
import { BASE_URL } from "../../utils/configConstants";

const ProductImageUpload = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const catId = searchParams.get('catId');
  const filter = searchParams.get('filter') || 'all';

  const [product, setProduct] = useState(null);
  const [images, setImages] = useState([]);
  const [imageColors, setImageColors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [showImageUploadModal, setShowImageUploadModal] = useState(false);
  const [selectedColorForUpload, setSelectedColorForUpload] = useState(null);
  const [uploadingImages, setUploadingImages] = useState([]);
  const [showImageDeleteModal, setShowImageDeleteModal] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);

  const showToast = (message, type = "error") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "" }), 2500);
  };

  // Fetch product details
  useEffect(() => {
    const fetchProductDetails = async () => {
      console.log('Fetching product details for ID:', productId);
      try {
        setLoading(true);
        const response = await getProductDetailsAPI(productId);

        if (response?.status === 200) {
          setProduct(response.data);
          console.log('Product data:', response.data);

          // Fetch existing images for the product
          if (response.data.images && response.data.images.length > 0) {
            setImages(response.data.images);

            // Set up imageColors for all images - using EditProduct logic
            const initialImageColors = response.data.images.map(img => {
              // Try to get color from different possible properties
              let colorHex = "";

              // First try to get from color object
              if (img?.color && img?.color.hex_code) {
                colorHex = img.color.hex_code;
              }
              // Then try direct hex_code
              else if (img?.hex_code) {
                colorHex = img?.hex_code;
              }
              // Then try color_id (but we need to find the actual hex_code for this)
              else if (img?.color_id && response.data?.colors) {
                const colorObj = response.data?.colors.find(c => c._id === img?.color_id);
                if (colorObj && colorObj?.hex_code) {
                  colorHex = colorObj?.hex_code;
                }
              }
              // Finally try color_hex_code (the original approach)
              else if (img?.color_hex_code) {
                colorHex = img?.color_hex_code;
              }

              // Only return color if it was actually found, otherwise return empty string
              return colorHex || "";
            });
            setImageColors(initialImageColors);
          }
        } else {
          setError(response?.message || 'Failed to fetch product details');
        }
      } catch (err) {
        console.error('Error fetching product details:', err);
        setError('Failed to fetch product details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (productId) {
      fetchProductDetails();
    }
  }, [productId]);

  // Convert file to base64
  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader?.result);
      reader.onerror = error => reject(error);
    });
  };

  // Handle image upload button click
  const handleImageUpload = (colorHex) => {
    const color = product.colors.find(c => c.hex_code === colorHex);
    if (color) {
      setSelectedColorForUpload(color);
      setShowImageUploadModal(true);
    }
  };

  // Handle image selection in modal
  const handleModalImageChange = (e) => {
    const files = Array?.from(e?.target?.files);

    // Validate file types
    const allowedTypes = ['image/png', 'image/jpg', 'image/jpeg'];
    const invalidFiles = files.filter(file => !allowedTypes.includes(file.type));

    if (invalidFiles.length > 0) {
      const invalidFileNames = invalidFiles.map(file => file.name).join(', ');
      showToast(`Invalid file format(s): ${invalidFileNames}. Only PNG, JPG, and JPEG files are allowed.`, "error");
      return;
    }

    // Process files and add to uploadingImages state
    const processFiles = async () => {
      const newImages = [];

      for (const file of files) {
        try {
          const base64 = await convertToBase64(file);
          newImages.push({
            file,
            base64,
            name: file.name,
            status: 'pending', // pending, uploading, uploaded, failed
            error: null
          });
        } catch (error) {
          console.error('Error converting image to base64:', error);
          newImages.push({
            file,
            base64: null,
            name: file.name,
            status: 'failed',
            error: 'Failed to process image'
          });
        }
      }

      setUploadingImages(prev => [...prev, ...newImages]);
    };

    processFiles();
  };

  // Upload single image
  const uploadSingleImage = async (imageData, index) => {
    setUploadingImages(prev =>
      prev.map((img, idx) =>
        idx === index ? { ...img, status: 'uploading', error: null } : img
      )
    );

    try {
      const uploadData = {
        product_id: product?._id,
        color_id: selectedColorForUpload?._id,
        image: imageData.base64
      };

      const response = await uploadImageAPI(uploadData);

      if (response?.status === 200) {
        setUploadingImages(prev =>
          prev.map((img, idx) =>
            idx === index ? { ...img, status: 'uploaded', ...response.data } : img
          )
        );

        // Add to main images array
        setImages(prev => [...prev, response.data]);
        setImageColors(prev => [...prev, selectedColorForUpload.hex_code]);

        return true;
      } else {
        throw new Error(response?.message || 'Upload failed');
      }
    } catch (error) {
      setUploadingImages(prev =>
        prev.map((img, idx) =>
          idx === index ? { ...img, status: 'failed', error: `Failed to upload ${imageData.name}` } : img
        )
      );
      return false;
    }
  };

  // Start uploading all pending images
  const startUploading = async () => {
    const pendingImages = uploadingImages.filter(img => img.status === 'pending');

    for (let i = 0; i < uploadingImages.length; i++) {
      if (uploadingImages[i].status === 'pending') {
        const success = await uploadSingleImage(uploadingImages[i], i);
        if (!success) {
          // Stop on first failure
          break;
        }
      }
    }
  };

  // Retry failed upload and continue with remaining
  const retryUpload = async (index) => {
    const success = await uploadSingleImage(uploadingImages[index], index);

    if (success) {
      // Continue with remaining pending images after this index
      for (let i = index + 1; i < uploadingImages.length; i++) {
        if (uploadingImages[i].status === 'pending') {
          const nextSuccess = await uploadSingleImage(uploadingImages[i], i);
          if (!nextSuccess) {
            // Stop if next upload fails
            break;
          }
        }
      }
    }
  };

  // Remove image from uploading list
  const removeUploadingImage = (index) => {
    setUploadingImages(prev => prev.filter((_, idx) => idx !== index));
  };

  // Close modal
  const closeImageUploadModal = () => {
    setShowImageUploadModal(false);
    setSelectedColorForUpload(null);
    setUploadingImages([]);
  };

  // Show image delete confirmation
  const handleRemoveImage = (idx) => {
    if (images.length <= 1) {
      showToast(
        "Product must have at least one image. Upload another image before deleting this one.",
        "error"
      );
      return;
    }
    setImageToDelete(idx);
    setShowImageDeleteModal(true);
  };

  // Confirm image deletion
  const confirmDeleteImage = async () => {
    if (imageToDelete === null) return;

    const imageToRemove = images[imageToDelete];

    // If image has an _id, it's from the API, so delete it from server
    if (imageToRemove._id) {
      try {
        const response = await deleteImageAPI({ image_id: imageToRemove._id });

        if (response?.status === 200) {
          // Remove from local state
          const newImages = images.filter((_, i) => i !== imageToDelete);
          const newImageColors = imageColors.filter((_, i) => i !== imageToDelete);

          setImages(newImages);
          setImageColors(newImageColors);

          showToast("Image deleted successfully!", "success");
        } else {
          showToast(`Failed to delete image: ${response?.message}`, "error");
        }
      } catch (error) {
        showToast(`Failed to delete image: ${error.message}`, "error");
      }
    } else {
      // If no _id, just remove from local state
      const newImages = images.filter((_, i) => i !== imageToDelete);
      const newImageColors = imageColors.filter((_, i) => i !== imageToDelete);

      setImages(newImages);
      setImageColors(newImageColors);
      showToast("Image removed successfully!", "success");
    }

    // Close modal
    setShowImageDeleteModal(false);
    setImageToDelete(null);
  };

  // Cancel image deletion
  const cancelDeleteImage = () => {
    setShowImageDeleteModal(false);
    setImageToDelete(null);
  };

  const handleBack = () => {
            const url = catId
              ? `/product?categoryId=${catId}`
              : `/product`;
    navigate(url, { state: { filter } });
  };

  if (loading) {
    return (
      <div className="pui-loading-container">
        <div className="pui-loading-spinner"></div>
        <p>Loading product details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pui-loading-container">
        <div style={{ color: '#dc2626', fontSize: '1.25rem', marginBottom: '1rem' }}>⚠️</div>
        <p style={{ color: '#dc2626', fontSize: '1.125rem' }}>{error}</p>
        <button onClick={handleBack} className="pui-back-button">
          <FaArrowLeft /> Back to Products
        </button>
      </div>
    );
  }

  return (
    <>
      <Toast toast={toast} />
      <div className="pui-product-main-content">
        <div className="pui-order-details-header">
          <button className="pui-back-button" onClick={handleBack}>
            <FaArrowLeft /> Back to Products
          </button>
        </div>

        <div className="pui-product-image-upload-container">
          <h2 className="pui-product-title">{product?.name}</h2>
          <p className="pui-product-description">Manage images for this product by color</p>

          <div className="pui-size-section">

            {product?.colors && product.colors.map((color) => {
              const colorImages = images.filter((img, idx) => {
                const imageColor = imageColors[idx] || "";
                return imageColor === color.hex_code;
              });

              return (
                <div className="pui-color-row" key={color._id} style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '5px'
                }}>
                  <div className="pui-color-label" style={{
                    fontWeight: 'bold',
                    color: '#333',
                    minWidth: '80px'
                  }}>
                    {color.name}
                  </div>
                  <div className="pui-image-boxes-container" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {images.map((img, idx) => {
                      const imageColor = imageColors[idx] || "";
                      if (imageColor === color.hex_code) {
                        return (
                          <div className="pui-image-preview-item" key={idx}>
                            <img src={BASE_URL+ img?.image} alt={`preview-${idx}`} className="pui-image-preview" />
                            <button
                              type="button"
                              className="pui-remove-image-btn"
                              onClick={() => handleRemoveImage(idx)}
                            >
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                                strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"></path>
                                <line x1="10" y1="11" x2="10" y2="17"></line>
                                <line x1="14" y1="11" x2="14" y2="17"></line>
                              </svg>
                            </button>
                          </div>
                        );
                      }
                      return null;
                    })}
                    {/* Add + box for this color */}
                    <div className="pui-image-upload-box" onClick={() => handleImageUpload(color.hex_code)}>
                      <span className="pui-plus-icon">+</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Image Upload Modal */}
      <ImageUploadModal
        show={showImageUploadModal}
        onClose={closeImageUploadModal}
        onFileChange={handleModalImageChange}
        selectedColor={selectedColorForUpload}
        uploadingImages={uploadingImages}
        onStartUploading={startUploading}
        onRetryUpload={retryUpload}
        onRemoveImage={removeUploadingImage}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        visible={showImageDeleteModal}
        onConfirm={confirmDeleteImage}
        onCancel={cancelDeleteImage}
        title="Confirm Deletion"
        message="Are you sure you want to delete this image?"
        confirmText="Delete"
      />
    </>
  );
};

export default ProductImageUpload;
